import { useEffect, useMemo, useState } from "react";
import "./index.css";
import { FormattedMessage, useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import {
  addContentLibraryDetails,
  setControllerCodes,
  setCustomerScopes,
} from "../../features/contentLibrarySlice";
import useConsentFragment from "../../hooks/useConsentFragment";
import ContentListing from "./ContentListing";
import Skeleton from "react-loading-skeleton";
import CustomSelect from "../CustomSelect";
import { getRegionFromHost, buildDocumentUrl } from "../../utils/host";
const ContentLibrary = ({ controllerCodeValue, customerScope }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const contentLibraryControllerCode = useSelector(
    (state) => state.contentLibrary.controllerCode
  );
  const contentLibraryCustomerScope = useSelector(
    (state) => state.contentLibrary.customerScope
  );
  const contentLibraryDetails = useSelector(
    (state) => state.contentLibrary.contentLibraryDetails
  );
  const { countryLanguages, countryLanguageStatus } = useSelector(
    (state) => state.controller
  );
  const languagesLoaded = useMemo(
    () => ["succeeded", "failed"]?.includes(countryLanguageStatus),
    [countryLanguageStatus]
  );
  const [contentLibrary, setContentLibrary] = useState([]);
  const [envProdButton, setEvnProdButton] = useState(false);
  const [envStageButton, setEvnStageButton] = useState(false);
  const [loading, setLoading] = useState(true);
  const [disabled, setDisabled] = useState(false);
  const [hostName, setHostName] = useState(() => {
    const host = globalThis.window?.location?.hostname ?? "";
    const regionFromHost = getRegionFromHost(host);
    return buildDocumentUrl("production", regionFromHost);
  });
  const [selectedOption, setSelectedOption] = useState(null);
  const {
    getVersionInfo,
    getDataPrivacyVersionsDatesInfo,
    getDraftEmailRemoteVersionsDatesInfo,
    getConsentRemoteCollectVersionsDatesInfo,
    getThankYouVersionsDatesInfo,
    getErrorVersionsDatesInfo,
    getUnsubCampaignVersionsDatesInfo,
    getUnsubCommercialVersionsDatesInfo,
    getUnsubMedicalVersionsDatesInfo,
    getUnsubThankYouVersionsDatesInfo,
    getUnsubErrorVersionsDatesInfo,
    getDraftMailUpdateVersionsDatesInfo,
    getSurveyConfirmationVersionsDatesInfo,
    getSurveyThankYouVersionsDatesInfo,
    getSurveyErrorVersionsDatesInfo,
  } = useConsentFragment();

  const region = useMemo(
    () => getRegionFromHost(globalThis.window?.location?.hostname ?? ""),
    []
  );

  const getVersionDetails = async (obj) => {
    const { controller, scope, language } = obj;
    const data = await getVersionInfo(controller, scope, language);

    return data;
  };
  const getDataPrivacyFragmentInfo = async (obj) => {
    const { controller, scope, language } = obj;
    const data = await getDataPrivacyVersionsDatesInfo(
      controller,
      scope,
      language
    );

    return data;
  };
  const getDraftEmailRemoteInfo = async (obj) => {
    const { controller, scope, language } = obj;
    const data = await getDraftEmailRemoteVersionsDatesInfo(
      controller,
      scope,
      language
    );

    return data;
  };
  const getConsentRemoteCollectInfo = async (obj) => {
    const { controller, scope, language } = obj;
    const data = await getConsentRemoteCollectVersionsDatesInfo(
      controller,
      scope,
      language
    );

    return data;
  };
  const getThankYouInfo = async (obj) => {
    const { controller, scope, language } = obj;
    const data = await getThankYouVersionsDatesInfo(
      controller,
      scope,
      language
    );

    return data;
  };
  const getErrorInfo = async (obj) => {
    const { controller, scope, language } = obj;
    const data = await getErrorVersionsDatesInfo(controller, scope, language);

    return data;
  };
  const getUnsubCampaignInfo = async (obj) => {
    const { controller, scope, language } = obj;
    const data = await getUnsubCampaignVersionsDatesInfo(
      controller,
      scope,
      language
    );

    return data;
  };
  const getUnsubCommercialInfo = async (obj) => {
    const { controller, scope, language } = obj;
    const data = await getUnsubCommercialVersionsDatesInfo(
      controller,
      scope,
      language
    );

    return data;
  };
  const getUnsubMedicalInfo = async (obj) => {
    const { controller, scope, language } = obj;
    const data = await getUnsubMedicalVersionsDatesInfo(
      controller,
      scope,
      language
    );

    return data;
  };
  const getUnsubThankYouInfo = async (obj) => {
    const { controller, scope, language } = obj;
    const data = await getUnsubThankYouVersionsDatesInfo(
      controller,
      scope,
      language
    );

    return data;
  };
  const getUnsubErrorInfo = async (obj) => {
    const { controller, scope, language } = obj;
    const data = await getUnsubErrorVersionsDatesInfo(
      controller,
      scope,
      language
    );

    return data;
  };
  const getDraftEmailUpdateInfo = async (obj) => {
    const { controller, scope, language } = obj;
    const data = await getDraftMailUpdateVersionsDatesInfo(
      controller,
      scope,
      language
    );

    return data;
  };

  const getSurveyConfirmationInfo = async (obj) => {
    const { controller, scope, language } = obj;
    const data = await getSurveyConfirmationVersionsDatesInfo(
      controller,
      scope,
      language
    );

    return data;
  };
  const getSurveyConfirmationThankYouInfo = async (obj) => {
    const { controller, scope, language } = obj;
    const data = await getSurveyThankYouVersionsDatesInfo(
      controller,
      scope,
      language
    );
    return data;
  };
  const getSurveyConfirmationErrorInfo = async (obj) => {
    const { controller, scope, language } = obj;
    const data = await getSurveyErrorVersionsDatesInfo(
      controller,
      scope,
      language
    );

    return data;
  };

  useEffect(() => {
    if (languagesLoaded && countryLanguages?.length > 0)
      setSelectedOption(countryLanguages[0]);
    else if (languagesLoaded) {
      setSelectedOption(null);
      setLoading(false);
    } else {
      setSelectedOption(null);
    }
  }, [countryLanguages, languagesLoaded]);

  const initialCalls = async (obj) => {
    setLoading(true);
    if (controllerCodeValue && customerScope) {
      const versionData = await getVersionDetails(obj);
      const dataPrivacyFragmentData = await getDataPrivacyFragmentInfo(obj);

      const draftEmailRemoteData = await getDraftEmailRemoteInfo(obj);

      const consentRemoteCollectData = await getConsentRemoteCollectInfo(obj);

      const thankYouData = await getThankYouInfo(obj);

      const errorData = await getErrorInfo(obj);

      const unsubCampaignData = await getUnsubCampaignInfo(obj);

      const unsubCommercialData = await getUnsubCommercialInfo(obj);

      const unsubMedicalData = await getUnsubMedicalInfo(obj);

      const unsubThankYouData = await getUnsubThankYouInfo(obj);

      const unsubErrorData = await getUnsubErrorInfo(obj);

      const draftEmailUpdateData = await getDraftEmailUpdateInfo(obj);

      const surveyConfirmationData = await getSurveyConfirmationInfo(obj);

      const surveyConfirmationThankYouData =
        await getSurveyConfirmationThankYouInfo(obj);

      const surveyConfirmationErrorData = await getSurveyConfirmationErrorInfo(
        obj
      );

      await fetchContentLibrary(
        versionData,
        dataPrivacyFragmentData,
        draftEmailRemoteData,
        consentRemoteCollectData,
        thankYouData,
        errorData,
        unsubCampaignData,
        unsubCommercialData,
        unsubMedicalData,
        unsubThankYouData,
        unsubErrorData,
        draftEmailUpdateData,
        surveyConfirmationData,
        surveyConfirmationThankYouData,
        surveyConfirmationErrorData
      );
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!controllerCodeValue || !customerScope || !countryLanguages?.length)
      return;
    if (
      contentLibraryDetails?.length > 0 &&
      controllerCodeValue === contentLibraryControllerCode &&
      customerScope === contentLibraryCustomerScope
    ) {
      setContentLibrary(contentLibraryDetails);
      handleProductionBtn();
      setLoading(false);
    } else {
      const obj = {
        controller: controllerCodeValue,
        scope: customerScope,
        language: countryLanguages[0]?.value,
      };

      initialCalls(obj);
    }
  }, [controllerCodeValue, customerScope, countryLanguages]);
  const handleLanguageChange = (e) => {
    setSelectedOption(e);
    const obj = {
      controller: controllerCodeValue,
      scope: customerScope,
      language: e?.value,
    };
    initialCalls(obj);
  };
  const fetchContentLibrary = async (
    versionInfo,
    dataPrivacyFragmentInfo,
    draftEmailRemoteInfo,
    consentRemoteCollectInfo,
    thankYouInfo,
    errorInfo,
    unsubCampaignInfo,
    unsubCommercialInfo,
    unsubMedicallInfo,
    unsubThankYouInfo,
    unsubErrorInfo,
    draftMailUpdateInfo,
    surveyConfirmationInfo,
    surveyThankYouInfo,
    surveyErrorInfo
  ) => {
    try {
      const response = await fetch(
        `${
          import.meta.env.VITE_DOMAIN
        }/${controllerCodeValue}/controller_content_library.json`
      );
      if (response.status === 404) {
        const responseCustomerScope = await fetch(
          `${
            import.meta.env.VITE_DOMAIN
          }/${controllerCodeValue}/${customerScope?.toLowerCase()}/controller_content_library.json`
        );
        const customerScopeData = await responseCustomerScope.json();
        const customerScopeDataUpdatedList = await customerScopeData?.list?.map(
          (docGroup) => ({
            ...docGroup, // keep Document_Report and Local_Report
            list: docGroup.list.map((item) => {
              if (item.URL_Pattern.includes("consent_statement") && versionInfo)
                return {
                  ...item,
                  ...versionInfo, // add versionInfo to each nested list item
                };
              if (
                item.URL_Pattern.includes("data_privacy_statement") &&
                dataPrivacyFragmentInfo
              )
                return {
                  ...item,
                  ...dataPrivacyFragmentInfo, // add versionInfo to each nested list item
                };
              if (
                item.URL_Pattern.includes("draft_email_remote") &&
                draftEmailRemoteInfo
              )
                return {
                  ...item,
                  ...dataPrivacyFragmentInfo,
                };
              if (
                item.URL_Pattern.includes("consent_remote_collect") &&
                consentRemoteCollectInfo
              )
                return {
                  ...item,
                  ...consentRemoteCollectInfo,
                };
              if (item.URL_Pattern.includes("consent_thankyou") && thankYouInfo)
                return {
                  ...item,
                  ...thankYouInfo,
                };
              if (item.URL_Pattern.includes("consent_error") && errorInfo)
                return {
                  ...item,
                  ...errorInfo,
                };
              if (
                item.URL_Pattern.includes("unsubscribe_campaign") &&
                unsubCampaignInfo
              )
                return {
                  ...item,
                  ...unsubCampaignInfo,
                };
              if (
                item.URL_Pattern.includes("commercial") &&
                unsubCommercialInfo
              )
                return {
                  ...item,
                  ...unsubCommercialInfo,
                };
              if (item.URL_Pattern.includes("medical") && unsubMedicallInfo)
                return {
                  ...item,
                  ...unsubMedicallInfo,
                };
              if (
                item.URL_Pattern.includes("uns_thankyou") &&
                unsubThankYouInfo
              )
                return {
                  ...item,
                  ...unsubThankYouInfo,
                };
              if (item.URL_Pattern.includes("uns_error") && unsubErrorInfo)
                return {
                  ...item,
                  ...unsubErrorInfo,
                };
              if (
                item.URL_Pattern.includes("draft_email_update") &&
                draftMailUpdateInfo
              )
                return {
                  ...item,
                  ...draftMailUpdateInfo,
                };
              if (
                item.URL_Pattern.includes("svy_confirmation") &&
                surveyConfirmationInfo
              )
                return {
                  ...item,
                  ...surveyConfirmationInfo,
                };
              if (
                item.URL_Pattern.includes("survey_thankyou/") &&
                surveyThankYouInfo
              )
                return {
                  ...item,
                  ...surveyThankYouInfo,
                };
              if (item.URL_Pattern.includes("survey_error") && surveyErrorInfo)
                return {
                  ...item,
                  ...surveyErrorInfo,
                };
              else
                return {
                  ...item,
                };
            }),
          })
        );

        setContentLibrary(customerScopeDataUpdatedList || []);
        setDisabled(false);
        handleProductionBtn();
        dispatch(addContentLibraryDetails(customerScopeDataUpdatedList));
        dispatch(setControllerCodes(controllerCodeValue));
        dispatch(setCustomerScopes(customerScope));
      } else {
        const data = await response.json();
        const updatedList = await data?.list?.map((docGroup) => ({
          ...docGroup, // keep Document_Report and Local_Report
          list: docGroup.list.map((item) => {
            if (item.URL_Pattern.includes("consent_statement") && versionInfo)
              return {
                ...item,
                ...versionInfo,
              };
            if (
              item.URL_Pattern.includes("data_privacy_statement") &&
              dataPrivacyFragmentInfo
            )
              return {
                ...item,
                ...dataPrivacyFragmentInfo,
              };
            if (
              item.URL_Pattern.includes("draft_email_remote") &&
              draftEmailRemoteInfo
            )
              return {
                ...item,
                ...dataPrivacyFragmentInfo,
              };
            if (
              item.URL_Pattern.includes("consent_remote_collect") &&
              consentRemoteCollectInfo
            )
              return {
                ...item,
                ...consentRemoteCollectInfo,
              };
            if (item.URL_Pattern.includes("consent_thankyou") && thankYouInfo)
              return {
                ...item,
                ...thankYouInfo,
              };
            if (item.URL_Pattern.includes("consent_error") && errorInfo)
              return {
                ...item,
                ...errorInfo,
              };
            if (
              item.URL_Pattern.includes("unsubscribe_campaign") &&
              unsubCampaignInfo
            )
              return {
                ...item,
                ...unsubCampaignInfo,
              };
            if (item.URL_Pattern.includes("commercial") && unsubCommercialInfo)
              return {
                ...item,
                ...unsubCommercialInfo,
              };
            if (item.URL_Pattern.includes("medical") && unsubMedicallInfo)
              return {
                ...item,
                ...unsubMedicallInfo,
              };
            if (item.URL_Pattern.includes("uns_thankyou") && unsubThankYouInfo)
              return {
                ...item,
                ...unsubThankYouInfo,
              };
            if (item.URL_Pattern.includes("uns_error") && unsubErrorInfo)
              return {
                ...item,
                ...unsubErrorInfo,
              };
            if (
              item.URL_Pattern.includes("draft_email_update") &&
              draftMailUpdateInfo
            )
              return {
                ...item,
                ...draftMailUpdateInfo,
              };
            if (
              item.URL_Pattern.includes("svy_confirmation") &&
              surveyConfirmationInfo
            )
              return {
                ...item,
                ...surveyConfirmationInfo,
              };
            if (
              item.URL_Pattern.includes("survey_thankyou/") &&
              surveyThankYouInfo
            )
              return {
                ...item,
                ...surveyThankYouInfo,
              };
            if (item.URL_Pattern.includes("survey_error") && surveyErrorInfo)
              return {
                ...item,
                ...surveyErrorInfo,
              };
            else
              return {
                ...item,
              };
          }),
        }));
        setContentLibrary(updatedList || []);
        dispatch(addContentLibraryDetails(updatedList));
        dispatch(setControllerCodes(controllerCodeValue));
        dispatch(setCustomerScopes(customerScope));
        setDisabled(false);
        handleProductionBtn();
      }
    } catch {
      //  uncomment the code to get data in local environment start
      // const { list } = sample;
      // setContentLibrary(list);
      //  uncomment the code to get data in local environment end
      setContentLibrary([]);
      handleProductionBtn();
      setDisabled(true);
    }
  };
  const handleProductionBtn = () => {
    setEvnProdButton(true);
    setEvnStageButton(false);
    setHostName(buildDocumentUrl("production", region));
  };
  const handleStagingBtn = () => {
    setEvnStageButton(true);
    setEvnProdButton(false);
    setHostName(buildDocumentUrl("staging", region));
  };

  return (
    <div className="contentlibrary">
      <h2 className="fs-3 fw-normal py-2">
        {languagesLoaded ? (
          <FormattedMessage
            id="label.contentlibrary"
            defaultMessage="Content Library"
          />
        ) : (
          <Skeleton height={50} enableAnimation={true} />
        )}
      </h2>
      <div className="search-bar-container px-3 py-4">
        {languagesLoaded ? (
          <section className="contentlibrary__header d-flex justify-content-between align-items-center flex-wrap gap-1">
            <p className="contentlibrary__header-description">
              The document links below can be updated by changing the language
              or environment:
            </p>
            <div className="contentlibrary__header-filters d-flex justify-content-between align-items-center gap-2">
              <div className="contentlibrary__header-filters__languagefilter">
                <CustomSelect
                  options={countryLanguages}
                  placeHolder={intl.formatMessage({
                    id: "id.new-placeholder.language",
                    defaultMessage: "Select Language",
                  })}
                  defaultValue={selectedOption?.value}
                  value={selectedOption?.value}
                  onChange={handleLanguageChange}
                  variant="select-secondary"
                />
              </div>
              <div
                className={`contentlibrary__header-filters__environmentfilter d-flex ${
                  disabled ? "not-allowed" : ""
                }`}
              >
                <button
                  type="button"
                  id="btn-production"
                  className={`btn btn-primary ${
                    envProdButton ? "active-button" : ""
                  }`}
                  onClick={handleProductionBtn}
                  disabled={disabled}
                >
                  <FormattedMessage id="id.prod" defaultMessage="Production" />
                </button>
                <button
                  type="button"
                  id="btn-staging"
                  className={`btn btn-primary ${
                    envStageButton ? "active-button" : ""
                  }`}
                  onClick={handleStagingBtn}
                  disabled={disabled}
                >
                  <FormattedMessage id="id.staging" defaultMessage="Staging" />
                </button>
              </div>
            </div>
          </section>
        ) : (
          <Skeleton height={50} enableAnimation={true} />
        )}
        {loading ? (
          <table className="table loading-table">
            <tbody>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]?.map((row) => (
                <tr key={row}>
                  {[1, 2, 3, 4, 5, 6]?.map((col) => (
                    <td key={col}>
                      <Skeleton animation="wave" variant="text" />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <>
            {!countryLanguages && (
              <div className="no-data-container">No Langauge Present</div>
            )}
            {controllerCodeValue &&
              customerScope &&
              contentLibrary &&
              selectedOption?.value && (
                <ContentListing
                  contentLibrary={contentLibrary}
                  controllerCodeValue={controllerCodeValue}
                  customerScope={customerScope}
                  hostname={hostName}
                  selectedOption={selectedOption?.value}
                />
              )}
          </>
        )}
      </div>
    </div>
  );
};

export default ContentLibrary;
